import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors';
import { useCabinetMemberMe, CABINET_MEMBER_ME_KEY } from '@/hooks/useCabinetMemberMe';
import { useAuth } from '@/components/AuthProvider';
import { ANNOUNCEMENTS } from './announcements';
import { selectLatestUnseen } from './selectLatestUnseen';

/**
 * Sélectionne l'unique annonce produit à afficher au membre courant, ou null.
 * Tire `seen_announcements` + `joined_at` du cache unique `useCabinetMemberMe`.
 * `joined_at` absent (membership pas encore résolue) → on n'affiche rien.
 */
export function useLatestUnseenAnnouncement() {
  const { data } = useCabinetMemberMe();
  // Rules-of-hooks : garder tout appel de hook AU-DESSUS de ce return.
  if (!data?.joined_at) return null;
  return selectLatestUnseen(ANNOUNCEMENTS, data.seen_announcements ?? [], data.joined_at);
}

/**
 * Marque des annonces comme vues côté serveur, puis invalide le cache
 * membership pour refléter l'état sans reload.
 *
 * Invalidation deux clés volontaire : `useCabinetMemberMe` scope sa query à
 * `[CABINET_MEMBER_ME_KEY, user?.id]`. Invalider `[CABINET_MEMBER_ME_KEY]`
 * couvre déjà le préfixe (TanStack invalide par préfixe), mais on garde la
 * clé exacte aussi — alignement avec la convention du repo (cf.
 * useMarkCabinetsAsVisited) et zéro risque comportemental.
 */
export function useMarkAnnouncementsSeen() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await apiClient.patch('/cabinet-members/announcements-seen', { ids });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CABINET_MEMBER_ME_KEY] });
      queryClient.invalidateQueries({ queryKey: [CABINET_MEMBER_ME_KEY, user?.id] });
    },
    // Fire-and-forget UX : pas de toast. On loggue + PostHog en silencieux pour
    // diagnostiquer si PATCH /cabinet-members/announcements-seen échoue (l'état
    // "vu" reste non persisté → la modale d'annonce réapparaîtra au prochain login).
    onError: (err) => {
      ErrorService.handle(err, {
        silent: true,
        context: { action: 'mark_announcements_seen', url: '/cabinet-members/announcements-seen' },
      });
    },
  });
}
